import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  ChartBarIcon,
  MinusIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
} from '@heroicons/react/24/outline';

const TrendGraph = ({
  trendData,
  onTimeRangeChange,
  onMetricToggle,
  selectedMetrics = ['overall'],
  timeRange = '6months',
}) => {
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const [visibleMetrics, setVisibleMetrics] = useState(selectedMetrics);
  const [currentTimeRange, setCurrentTimeRange] = useState(timeRange);

  const timeRanges = [
    { key: '1month', label: '1 Month', days: 30 },
    { key: '3months', label: '3 Months', days: 90 },
    { key: '6months', label: '6 Months', days: 180 },
    { key: '1year', label: '1 Year', days: 365 },
  ];

  const metrics = [
    {
      key: 'overall',
      name: 'Overall Health',
      color: 'blue',
      gradient: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-100',
      textColor: 'text-blue-700',
      icon: ChartBarIcon,
    },
    {
      key: 'finance',
      name: 'Finance',
      color: 'emerald',
      gradient: 'from-emerald-500 to-emerald-600',
      bgColor: 'bg-emerald-100',
      textColor: 'text-emerald-700',
      icon: ChartBarIcon,
    },
    {
      key: 'sales',
      name: 'Sales',
      color: 'amber',
      gradient: 'from-amber-500 to-amber-600',
      bgColor: 'bg-amber-100',
      textColor: 'text-amber-700',
      icon: ChartBarIcon,
    },
    {
      key: 'operations',
      name: 'Operations',
      color: 'purple',
      gradient: 'from-purple-500 to-purple-600',
      bgColor: 'bg-purple-100',
      textColor: 'text-purple-700',
      icon: ChartBarIcon,
    },
    {
      key: 'hr',
      name: 'HR',
      color: 'orange',
      gradient: 'from-orange-500 to-orange-600',
      bgColor: 'bg-orange-100',
      textColor: 'text-orange-700',
      icon: ChartBarIcon,
    },
    {
      key: 'compliance',
      name: 'Compliance',
      color: 'red',
      gradient: 'from-red-500 to-red-600',
      bgColor: 'bg-red-100',
      textColor: 'text-red-700',
      icon: ChartBarIcon,
    },
  ];

  const getMetricStyle = metricKey => {
    const metric = metrics.find(m => m.key === metricKey);
    return metric || metrics[0];
  };

  const getTrendIcon = trend => {
    if (trend > 0) return ArrowTrendingUpIcon;
    if (trend < 0) return ArrowTrendingDownIcon;
    return MinusIcon;
  };

  const getTrendColor = trend => {
    if (trend > 0) return 'text-emerald-600';
    if (trend < 0) return 'text-red-600';
    return 'text-gray-600';
  };

  const formatDate = dateString => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  const getYAxisLabels = () => {
    return [100, 80, 60, 40, 20, 0];
  };

  const getDataPoints = metricKey => {
    const data = trendData[metricKey] || [];
    return data.filter(point => {
      const pointDate = new Date(point.date);
      const cutoffDate = new Date();
      cutoffDate.setDate(
        cutoffDate.getDate() - timeRanges.find(r => r.key === currentTimeRange)?.days
      );
      return pointDate >= cutoffDate;
    });
  };

  const getMaxValue = () => {
    let max = 0;
    visibleMetrics.forEach(metricKey => {
      const points = getDataPoints(metricKey);
      points.forEach(point => {
        if (point.value > max) max = point.value;
      });
    });
    return Math.max(max, 100);
  };

  const getMinValue = () => {
    let min = 100;
    visibleMetrics.forEach(metricKey => {
      const points = getDataPoints(metricKey);
      points.forEach(point => {
        if (point.value < min) min = point.value;
      });
    });
    return Math.min(min, 0);
  };

  const handleTimeRangeChange = range => {
    setCurrentTimeRange(range);
    if (onTimeRangeChange) {
      onTimeRangeChange(range);
    }
  };

  const handleMetricToggle = metricKey => {
    const newVisibleMetrics = visibleMetrics.includes(metricKey)
      ? visibleMetrics.filter(m => m !== metricKey)
      : [...visibleMetrics, metricKey];
    setVisibleMetrics(newVisibleMetrics);
    if (onMetricToggle) {
      onMetricToggle(metricKey, newVisibleMetrics);
    }
  };

  const getChartHeight = () => 300;
  const getChartWidth = () => 800;

  const getXPosition = (date, dataPoints) => {
    if (dataPoints.length <= 1) return 50;
    const firstDate = new Date(dataPoints[0].date);
    const lastDate = new Date(dataPoints[dataPoints.length - 1].date);
    const totalDays = (lastDate - firstDate) / (1000 * 60 * 60 * 24);
    const daysFromStart = (new Date(date) - firstDate) / (1000 * 60 * 60 * 24);
    return 50 + (daysFromStart / totalDays) * (getChartWidth() - 100);
  };

  const getYPosition = value => {
    const height = getChartHeight();
    const max = getMaxValue();
    const min = getMinValue();
    const range = max - min;
    return height - 50 - ((value - min) / range) * (height - 100);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm md:p-6"
    >
      <div className="mb-4 flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center md:mb-6">
        <div>
          <h3 className="text-xl font-bold text-gray-900">Health Trends</h3>
          <p className="text-gray-600">Track your business health over time</p>
        </div>

        <div className="flex items-center gap-2">
          {timeRanges.map(range => (
            <motion.button
              key={range.key}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => handleTimeRangeChange(range.key)}
              className={`rounded-lg px-3 py-2 text-xs font-medium transition-all duration-200 md:text-sm ${
                currentTimeRange === range.key
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {range.label}
            </motion.button>
          ))}
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2 md:mb-6 md:gap-3">
        {metrics.map(metric => {
          const isVisible = visibleMetrics.includes(metric.key);
          const MetricIcon = metric.icon;

          return (
            <motion.button
              key={metric.key}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => handleMetricToggle(metric.key)}
              className={`flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium transition-all duration-200 md:text-sm ${
                isVisible
                  ? `${metric.bgColor} ${metric.textColor} shadow-md`
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <MetricIcon className="h-4 w-4" />
              {metric.name}
            </motion.button>
          );
        })}
      </div>

      <div className="relative overflow-hidden rounded-lg border border-gray-200 bg-gray-50 p-4">
        <svg
          width={getChartWidth()}
          height={getChartHeight()}
          className="mx-auto"
          viewBox={`0 0 ${getChartWidth()} ${getChartHeight()}`}
        >
          <g className="text-gray-500">
            {getYAxisLabels().map(label => (
              <g key={label}>
                <line
                  x1="50"
                  y1={getYPosition(label)}
                  x2={getChartWidth() - 50}
                  y2={getYPosition(label)}
                  stroke="currentColor"
                  strokeWidth="0.5"
                  opacity="0.3"
                />
                <text x="40" y={getYPosition(label) + 4} textAnchor="end" className="text-xs">
                  {label}%
                </text>
              </g>
            ))}
          </g>

          <line
            x1="50"
            y1={getChartHeight() - 50}
            x2={getChartWidth() - 50}
            y2={getChartHeight() - 50}
            stroke="currentColor"
            strokeWidth="1"
            opacity="0.5"
          />

          {visibleMetrics.map(metricKey => {
            const dataPoints = getDataPoints(metricKey);
            if (dataPoints.length < 2) return null;

            const metric = getMetricStyle(metricKey);
            const points = dataPoints.map(point => ({
              x: getXPosition(point.date, dataPoints),
              y: getYPosition(point.value),
              value: point.value,
              date: point.date,
            }));

            return (
              <g key={metricKey}>
                <path
                  d={`M ${points.map(p => `${p.x},${p.y}`).join(' L ')}`}
                  fill="none"
                  stroke={`url(#${metricKey}Gradient)`}
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />

                <defs>
                  <linearGradient id={`${metricKey}Gradient`} x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor={metric.gradient.split(' ')[1]} />
                    <stop offset="100%" stopColor={metric.gradient.split(' ')[3]} />
                  </linearGradient>
                </defs>

                {points.map((point, index) => (
                  <motion.circle
                    key={index}
                    cx={point.x}
                    cy={point.y}
                    r="4"
                    fill={`url(#${metricKey}Gradient)`}
                    stroke="white"
                    strokeWidth="2"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: index * 0.1 }}
                    onMouseEnter={() => setHoveredPoint({ ...point, metric: metricKey })}
                    onMouseLeave={() => setHoveredPoint(null)}
                    className="cursor-pointer"
                  />
                ))}
              </g>
            );
          })}
        </svg>

        {hoveredPoint && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="absolute rounded-lg border border-gray-200 bg-white p-3 shadow-lg"
            style={{
              left: hoveredPoint.x + 20,
              top: hoveredPoint.y - 40,
            }}
          >
            <div className="text-sm font-medium text-gray-900">
              {getMetricStyle(hoveredPoint.metric).name}
            </div>
            <div className="text-lg font-bold text-blue-600">{hoveredPoint.value}%</div>
            <div className="text-xs text-gray-500">{formatDate(hoveredPoint.date)}</div>
          </motion.div>
        )}
      </div>
      <div className="mt-4 flex flex-wrap items-center justify-center gap-4">
        {visibleMetrics.map(metricKey => {
          const metric = getMetricStyle(metricKey);
          const dataPoints = getDataPoints(metricKey);
          const latestValue = dataPoints[dataPoints.length - 1]?.value || 0;
          const previousValue = dataPoints[dataPoints.length - 2]?.value || latestValue;
          const trend = latestValue - previousValue;
          const TrendIcon = getTrendIcon(trend);

          return (
            <div key={metricKey} className="flex items-center gap-2">
              <div className={`h-3 w-3 rounded-full bg-gradient-to-r ${metric.gradient}`} />
              <span className="text-sm font-medium text-gray-700">{metric.name}</span>
              <span className="text-sm text-gray-500">{latestValue}%</span>
              <TrendIcon className={`h-4 w-4 ${getTrendColor(trend)}`} />
            </div>
          );
        })}
      </div>
    </motion.div>
  );
};

export default TrendGraph;
